import React, { useState, useEffect, useCallback, useRef } from 'react';
import contractorService, { Contractor, ContractorFilter } from '../../services/contractorService';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Chip,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Backdrop,
  Divider,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '../icons/EditIcon';
import AddIcon from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import DragIndicator from '@mui/icons-material/DragIndicator';
import { useNotification } from '../../components/layouts/Layout';
import EditContractorDialog from './EditContractorDialog';
import AddOrderDialog from './AddOrderDialog';

interface ContractorsTabProps {
  searchText: string;
  location: string;
  nipText?: string;
  industry?: string;
}


const ContractorsTab: React.FC<ContractorsTabProps> = ({
  searchText,
  location,
  nipText = '',
  industry = '',
}) => {
  const [categoryValue, setCategoryValue] = useState(0);
  const [contractorsData, setContractorsData] = useState<Contractor[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [tableHeight, setTableHeight] = useState<number | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { showNotification } = useNotification();

  // Kolumny tabeli
  const ALL_COLUMNS = ['branza', 'nazwa', 'adres', 'miejscowosc', 'nip', 'email', 'telefon', 'ocena', 'akcja'];
  const COLUMNS_LABELS: Record<string, string> = {
    branza: 'Branża',
    nazwa: 'Nazwa',
    adres: 'Adres',
    miejscowosc: 'Miejscowość',
    nip: 'NIP',
    email: 'E-mail',
    telefon: 'Telefon',
    ocena: 'Ocena',
    akcja: 'Akcja',
  };

  // Stan dla dostosowywania kolumn
  const [columnsVisible, setColumnsVisible] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('contractors_columns_visible');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Upewnij się, że wszystkie kolumny są zdefiniowane
        const result: Record<string, boolean> = {};
        ALL_COLUMNS.forEach(col => {
          result[col] = parsed[col] !== false;
        });
        return result;
      }
    } catch {}
    // Domyślnie wszystkie kolumny widoczne
    const defaultVisible: Record<string, boolean> = {};
    ALL_COLUMNS.forEach(col => {
      defaultVisible[col] = true;
    });
    return defaultVisible;
  });
  const [draftColumnsVisible, setDraftColumnsVisible] = useState<Record<string, boolean> | null>(null);
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('contractors_columns_order');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Upewnij się, że wszystkie kolumny są w kolejności
        const result = parsed.filter((col: string) => ALL_COLUMNS.includes(col));
        const missing = ALL_COLUMNS.filter(col => !result.includes(col));
        return [...result, ...missing];
      }
    } catch {}
    return [...ALL_COLUMNS];
  });
  const [draftOrder, setDraftOrder] = useState<string[] | null>(null);
  const [colsOpen, setColsOpen] = useState(false);
  const [colsPosition, setColsPosition] = useState<{ top?: number; left?: number }>({});
  const [colsDragging, setColsDragging] = useState<{ startX: number; startY: number; top?: number; left?: number } | null>(null);
  const [colResizeMode, setColResizeMode] = useState(false);
  
  // Stan dla menu kontekstowego
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedContractor, setSelectedContractor] = useState<Contractor | null>(null);
  const openMenu = Boolean(menuAnchorEl);
  
  // Stan dla dialogów
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  
  // Obsługa otwierania menu
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, contractor: Contractor) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedContractor(contractor);
  };
  
  // Obsługa zamykania menu
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  // Obsługa edycji kontrahenta
  const handleEditContractor = () => {
    handleMenuClose();
    setEditDialogOpen(true);
  };
  
  // Obsługa dodawania zamówienia
  const handleAddOrder = () => {
    handleMenuClose();
    setOrderDialogOpen(true);
  };
  
  // Obsługa dodawania umowy
  const handleAddContract = () => {
    handleMenuClose();
    showNotification('Funkcja dodawania umowy będzie dostępna wkrótce', 'info');
  };
  
  // Obsługa sukcesu edycji kontrahenta
  const handleEditSuccess = () => {
    // Resetuj dane i załaduj od początku
    setContractorsData([]);
    setCurrentPage(1);
    setHasMore(true);
    loadInitialData();
  };
  
  // Obsługa sukcesu dodania zamówienia
  const handleOrderSuccess = () => {
    showNotification('Zamówienie zostało dodane pomyślnie', 'success');
  };

  const LIMIT = 100;

  // Pobieranie danych kontrahentów z API
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setCurrentPage(1);
      setHasMore(true);
      
      const filters: ContractorFilter = {};
      if (searchText) {
        filters.searchText = searchText;
      }
      if (location) {
        filters.location = location;
      }
      if (nipText) {
        filters.nipText = nipText;
      }
      if (industry) {
        filters.industry = industry;
      }
      
      // Dodanie filtrowania po kategorii
      if (categoryValue === 1) { // Dostawcy
        filters.type = 'dostawca';
      } else if (categoryValue === 2) { // Podwykonawcy
        filters.type = 'podwykonawca';
      } else if (categoryValue === 3) { // Inwestorzy
        filters.type = 'inwestor';
      }
      
      const response = await contractorService.filterContractors(filters, 1, LIMIT);
      setContractorsData(response.contractors);
      setTotalCount(response.pagination.total);
      setHasMore(response.contractors.length === LIMIT && response.contractors.length < response.pagination.total);
      setCurrentPage(1);
    } catch (error) {
      console.error('Błąd podczas pobierania kontrahentów:', error);
      setContractorsData([]);
    } finally {
      setLoading(false);
    }
  }, [searchText, location, nipText, industry, categoryValue]);

  // Ładowanie kolejnych danych (infinite scroll)
  const loadMoreData = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    
    try {
      setLoadingMore(true);
      const nextPage = currentPage + 1;
      
      const filters: ContractorFilter = {};
      if (searchText) {
        filters.searchText = searchText;
      }
      if (location) {
        filters.location = location;
      }
      if (nipText) {
        filters.nipText = nipText;
      }
      if (industry) {
        filters.industry = industry;
      }
      
      // Dodanie filtrowania po kategorii
      if (categoryValue === 1) { // Dostawcy
        filters.type = 'dostawca';
      } else if (categoryValue === 2) { // Podwykonawcy
        filters.type = 'podwykonawca';
      } else if (categoryValue === 3) { // Inwestorzy
        filters.type = 'inwestor';
      }
      
      const response = await contractorService.filterContractors(filters, nextPage, LIMIT);
      setContractorsData(prev => {
        const newData = [...prev, ...response.contractors];
        const hasMoreData = response.contractors.length === LIMIT && newData.length < response.pagination.total;
        setHasMore(hasMoreData);
        return newData;
      });
      setCurrentPage(nextPage);
    } catch (error) {
      console.error('Błąd podczas ładowania kolejnych kontrahentów:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [currentPage, hasMore, loadingMore, searchText, location, nipText, industry, categoryValue]);

  // Pobieranie danych przy pierwszym renderowaniu i zmianie filtrów
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Nasłuchiwanie na zdarzenie 'search'
  useEffect(() => {
    const handleSearch = () => {
      loadInitialData();
    };

    window.addEventListener('search', handleSearch);
    return () => {
      window.removeEventListener('search', handleSearch);
    };
  }, [loadInitialData]);

  // Obsługa infinite scroll
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      // Załaduj więcej gdy użytkownik jest 100px od końca
      if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !loadingMore) {
        loadMoreData();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [hasMore, loadingMore, loadMoreData]);

  const handleCategoryChange = (event: React.SyntheticEvent, newValue: number) => {
    setCategoryValue(newValue);
    // Reset danych przy zmianie kategorii - loadInitialData zostanie wywołane przez useEffect
  };

  // Obsługa przeciągania okna "Dostosuj kolumny"
  useEffect(() => {
    if (!colsDragging) return;
    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - colsDragging.startX;
      const deltaY = e.clientY - colsDragging.startY;
      setColsPosition({
        top: Math.max(0, Math.min(window.innerHeight - 400, (colsDragging.top || window.innerHeight) + deltaY)),
        left: Math.max(0, Math.min(window.innerWidth - 280, (colsDragging.left || window.innerWidth) + deltaX)),
      });
    };
    const handleMouseUp = () => {
      setColsDragging(null);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [colsDragging]);

  // ESC zamyka okno "Dostosuj kolumny"
  useEffect(() => {
    if (!colsOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setColsOpen(false);
        setColResizeMode(false);
        setDraftColumnsVisible(null);
        setDraftOrder(null);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [colsOpen]);

  // Obliczanie wysokości tabeli na podstawie dostępnej przestrzeni
  useEffect(() => {
    const measureHeights = () => {
      try {
        const root = tableContainerRef.current as HTMLElement | null;
        if (!root) return;
        const top = root.getBoundingClientRect().top;
        const viewportAvail = Math.max(300, Math.floor((window.innerHeight || 0) - top - 80)); // 80px na footer i marginesy
        setTableHeight(viewportAvail);
      } catch {}
    };
    const t = setTimeout(measureHeights, 0);
    const t2 = setTimeout(measureHeights, 100);
    window.addEventListener('resize', measureHeights);
    return () => { 
      clearTimeout(t); 
      clearTimeout(t2); 
      window.removeEventListener('resize', measureHeights); 
    };
  }, [contractorsData.length]);

  // Kategorie kontrahentów
  const categories = [
    'Wszyscy',
    'Dostawcy',
    'Podwykonawcy',
    'Inwestorzy',
  ];

  // Liczniki dla każdej kategorii
  const getCategoryCount = (categoryIndex: number) => {
    if (categoryIndex === 0) {
      return totalCount; // Wszyscy
    }
    // Dla innych kategorii możemy policzyć z załadowanych danych lub użyć totalCount
    // Na razie zwracamy totalCount, można później zoptymalizować
    return contractorsData.filter(c => {
      if (categoryIndex === 1) return c.type === 'dostawca';
      if (categoryIndex === 2) return c.type === 'podwykonawca';
      if (categoryIndex === 3) return c.type === 'inwestor';
      return true;
    }).length;
  };


  // Obsługa aktualizacji kontrahentów z MSSQL
  const handleUpdateFromMssql = async () => {
    try {
      const result = await contractorService.updateContractorsFromMssql();
      showNotification(
        `Aktualizacja zakończona: Dodano ${result.stats.created}, zaktualizowano ${result.stats.updated} kontrahentów`,
        'success'
      );
      // Odśwież dane po aktualizacji
      loadInitialData();
    } catch (error) {
      console.error('Błąd podczas aktualizacji kontrahentów z MSSQL:', error);
      showNotification(
        'Wystąpił błąd podczas aktualizacji kontrahentów z MSSQL',
        'error'
      );
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '2px', height: 40, mt: '12px', mb: 0, flexGrow: 1 }}>
          <Tabs
            value={categoryValue}
            onChange={handleCategoryChange}
            aria-label="contractor categories"
            sx={{ 
              minHeight: 40,
              height: 40,
              flexGrow: 1,
              '& .MuiTabs-indicator': { display: 'none' },
              '& .MuiTabs-flexContainer': { gap: '2px' }
            }}
          >
            {categories.map((category, index) => (
              <Tab 
                key={index}
                disableRipple
                label={`${category} (${getCategoryCount(index)})`}
                sx={{
                  textTransform: 'none',
                  color: '#020242',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  px: 3,
                  minHeight: 32,
                  height: 32,
                  mt: '8px',
                  bgcolor: '#F1F1F6',
                  borderRadius: '4px 4px 0 0',
                  '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                  '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
                }}
              />
            ))}
          </Tabs>
        </Box>
        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', mt: '26.5px' }}>
          <IconButton
            size="small"
            onClick={handleUpdateFromMssql}
            sx={{
              width: 32,
              height: 32,
              borderRadius: '4px',
              p: 0,
              bgcolor: 'transparent',
              transition: 'background-color 0.15s ease',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
            }}
          >
            <Box
              component="img"
              alt="aktualizuj"
              src="/assets/icons/update_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg"
              sx={{ width: 24, height: 24 }}
            />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              setDraftColumnsVisible({ ...columnsVisible });
              setDraftOrder([...columnOrder]);
              setColsOpen(true);
              setColResizeMode(true);
            }}
            sx={{
              width: 32,
              height: 32,
              borderRadius: '4px',
              p: 0,
              bgcolor: colsOpen ? '#E0E0F5' : 'transparent',
              transition: 'background-color 0.15s ease',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
            }}
          >
            <Box
              component="img"
              alt="kolumny"
              src="/assets/icons/frame-344.svg"
              sx={{ width: 32, height: 32 }}
            />
          </IconButton>
        </Box>
      </Box>

      <TableContainer 
        ref={tableContainerRef}
        component={Paper} 
        sx={{ 
          height: tableHeight ?? 432, 
          overflow: 'auto', 
          position: 'relative', 
          border: '1px solid #d0d0d6', 
          borderBottom: '1px solid #d0d0d6', 
          borderRadius: 0, 
          borderTopRightRadius: 4, 
          borderBottomLeftRadius: 4, 
          borderBottomRightRadius: 4, 
          overflowX: 'hidden', 
          overflowY: 'auto', 
          boxShadow: 'none', 
          scrollbarWidth: 'none', 
          msOverflowStyle: 'none', 
          '&::-webkit-scrollbar': { width: 0, height: 0 } 
        }}
      >
        <Table 
          size="small" 
          stickyHeader 
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& .MuiTableCell-root': { 
              padding: '1px 4px', 
              fontSize: '0.85rem', 
              borderRadius: 0 
            },
            '& .MuiTableCell-head': { 
              position: 'sticky', 
              top: 0, 
              zIndex: 2, 
              backgroundColor: '#FAFAFA', 
              borderRight: '1px solid #d4d4d9', 
              borderRadius: 0, 
              boxShadow: 'none' 
            },
            '& .MuiTableCell-body': { 
              borderRight: 'none', 
              borderRadius: 0 
            },
            '& .MuiTableRow-head > *:last-child': { 
              borderRight: 'none', 
              borderTopRightRadius: 4 
            }
          }}
        >
          <TableHead>
            <TableRow>
              {(draftOrder ?? columnOrder).map((colKey) => {
                if ((draftColumnsVisible ?? columnsVisible)[colKey] === false) return null;
                const isLast = (draftOrder ?? columnOrder).filter(c => (draftColumnsVisible ?? columnsVisible)[c] !== false).indexOf(colKey) === (draftOrder ?? columnOrder).filter(c => (draftColumnsVisible ?? columnsVisible)[c] !== false).length - 1;
                const widths: Record<string, string> = {
                  branza: '100px',
                  nazwa: '200px',
                  adres: '150px',
                  miejscowosc: '120px',
                  nip: '120px',
                  email: '200px',
                  telefon: '120px',
                  ocena: '80px',
                  akcja: '60px',
                };
                return (
                  <TableCell 
                    key={colKey}
                    sx={{ 
                      width: widths[colKey] || '100px',
                      position: 'sticky', 
                      top: 0, 
                      zIndex: 2, 
                      backgroundColor: '#FAFAFA', 
                      borderRight: isLast ? 'none' : '1px solid #d4d4d9',
                      ...(isLast && { borderTopRightRadius: 4 })
                    }}
                  >
                    {COLUMNS_LABELS[colKey] || colKey}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={(draftOrder ?? columnOrder).filter(c => (draftColumnsVisible ?? columnsVisible)[c] !== false).length} align="center">Ładowanie danych...</TableCell>
              </TableRow>
            ) : (contractorsData?.length || 0) === 0 ? (
              <TableRow>
                <TableCell colSpan={(draftOrder ?? columnOrder).filter(c => (draftColumnsVisible ?? columnsVisible)[c] !== false).length} align="center">Brak danych do wyświetlenia</TableCell>
              </TableRow>
            ) : (
              contractorsData?.map((contractor) => {
                const renderCell = (colKey: string) => {
                  switch (colKey) {
                    case 'branza':
                      return <TableCell key={colKey} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{contractor.industry}</TableCell>;
                    case 'nazwa':
                      return (
                        <TableCell key={colKey} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <Tooltip title={contractor.name} placement="top">
                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {contractor.name}
                            </Box>
                          </Tooltip>
                        </TableCell>
                      );
                    case 'adres':
                      return <TableCell key={colKey} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{contractor.address}</TableCell>;
                    case 'miejscowosc':
                      return <TableCell key={colKey} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{contractor.location}</TableCell>;
                    case 'nip':
                      return <TableCell key={colKey} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{contractor.nip}</TableCell>;
                    case 'email':
                      return (
                        <TableCell key={colKey} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {contractor.email === 'brak' || contractor.email === 'brak email' || contractor.email === 'Brak email' || contractor.email === 'Brak' ? (
                            contractor.email2 && contractor.email2.trim() !== '' ? (
                              <>{contractor.email2}</>
                            ) : (
                              <>brak email</>
                            )
                          ) : (
                            <>
                              {contractor.email}
                              {contractor.email2 && contractor.email2.trim() !== '' && (
                                <>, {contractor.email2}</>
                              )}
                            </>
                          )}
                        </TableCell>
                      );
                    case 'telefon':
                      return <TableCell key={colKey} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{contractor.phone}</TableCell>;
                    case 'ocena':
                      return (
                        <TableCell key={colKey} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <Chip
                            label={contractor.rating}
                            size="small"
                            sx={{
                              backgroundColor:
                                contractor.rating === 'dobry'
                                  ? '#e6f7f0'
                                  : contractor.rating === 'średni'
                                  ? '#fff3e6'
                                  : '#ffe6e6',
                              color:
                                contractor.rating === 'dobry'
                                  ? '#00a36e'
                                  : contractor.rating === 'średni'
                                  ? '#ff9933'
                                  : '#ff3333',
                              fontSize: '0.7rem',
                              height: '20px',
                            }}
                          />
                        </TableCell>
                      );
                    case 'akcja':
                      return (
                        <TableCell key={colKey} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <IconButton 
                            size="small" 
                            onClick={(e) => handleMenuOpen(e, contractor)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      );
                    default:
                      return null;
                  }
                };

                return (
                  <TableRow
                    hover
                    key={contractor.id}
                    sx={{ '&:hover': { backgroundColor: '#F5F5FF' } }}
                  >
                    {(draftOrder ?? columnOrder).map((colKey) => {
                      if ((draftColumnsVisible ?? columnsVisible)[colKey] === false) return null;
                      return renderCell(colKey);
                    })}
                  </TableRow>
                );
              })
            )}
            {loadingMore && (
              <TableRow>
                <TableCell colSpan={(draftOrder ?? columnOrder).filter(c => (draftColumnsVisible ?? columnsVisible)[c] !== false).length} align="center" sx={{ py: 2 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Informacja o liczbie załadowanych kontrahentów */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" component="span">
          Wyświetlono: {contractorsData.length} z {totalCount} kontrahentów
        </Typography>
      </Box>

      {/* Menu kontekstowe dla akcji na kontrahentach */}
      <Menu
        anchorEl={menuAnchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: { minWidth: 200 }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleEditContractor}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edytuj</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleAddOrder}>
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Dodaj zamówienie</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleAddContract}>
          <ListItemIcon>
            <Box
              component="svg"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 -960 960 960"
              sx={{ fill: 'currentColor', display: 'inline-block' }}
            >
              <path d="M212.31-140Q182-140 161-161q-21-21-21-51.31v-535.38Q140-778 161-799q21-21 51.31-21h535.38Q778-820 799-799q21 21 21 51.31v535.38Q820-182 799-161q-21 21-51.31 21H212.31Zm0-60h535.38q4.62 0 8.46-3.85 3.85-3.84 3.85-8.46v-535.38q0-4.62-3.85-8.46-3.84-3.85-8.46-3.85H212.31q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46v535.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85ZM200-760v560-560Zm120 470h200q12.77 0 21.38-8.62Q550-307.23 550-320t-8.62-21.38Q532.77-350 520-350H320q-12.77 0-21.38 8.62Q290-332.77 290-320t8.62 21.38Q307.23-290 320-290Zm0-160h320q12.77 0 21.38-8.62Q670-467.23 670-480t-8.62-21.38Q652.77-510 640-510H320q-12.77 0-21.38 8.62Q290-492.77 290-480t8.62 21.38Q307.23-450 320-450Zm0-160h320q12.77 0 21.38-8.62Q670-627.23 670-640t-8.62-21.38Q652.77-670 640-670H320q-12.77 0-21.38 8.62Q290-652.77 290-640t8.62 21.38Q307.23-610 320-610Z"/>
            </Box>
          </ListItemIcon>
          <ListItemText>Dodaj umowę</ListItemText>
        </MenuItem>
      </Menu>

      {/* Dialog edycji kontrahenta */}
      {selectedContractor && (
        <EditContractorDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          contractor={selectedContractor}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Dialog dodawania zamówienia */}
      {selectedContractor && (
        <AddOrderDialog
          open={orderDialogOpen}
          onClose={() => setOrderDialogOpen(false)}
          contractor={selectedContractor}
          onSuccess={handleOrderSuccess}
        />
      )}

      {/* Okno „Dostosuj kolumny" */}
      <Backdrop open={colsOpen} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1, backgroundColor: 'rgba(0,0,0,0.35)', pointerEvents: 'none' }}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            right: 0,
            height: '50vh',
            width: '280px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0px 5px 30px rgba(0,0,0,0.15)',
            borderRadius: 0,
            zIndex: (theme) => theme.zIndex.modal + 2,
            backgroundColor: '#fff',
            top: colsPosition.top ? `${colsPosition.top}px` : undefined,
            left: colsPosition.left ? `${colsPosition.left}px` : undefined,
            pointerEvents: 'auto'
          }}
          onClick={(e)=> e.stopPropagation()}
        >
          <Box
            sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto', position: 'sticky', top: 0, bgcolor: '#FFFFFF', zIndex: 1, cursor: 'move', userSelect: 'none' }}
            onMouseDown={(e)=> setColsDragging({ startX: e.clientX, startY: e.clientY, top: colsPosition.top, left: colsPosition.left })}
          >
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>Dostosuj kolumny</Typography>
            <IconButton size="small" onClick={()=>{ setColsOpen(false); setColResizeMode(false); setDraftColumnsVisible(null); setDraftOrder(null); }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            {(() => {
              const list = draftColumnsVisible ?? columnsVisible;
              if (draftColumnsVisible == null) setDraftColumnsVisible({ ...columnsVisible });
              const ord = draftOrder ?? columnOrder;
              if (draftOrder == null) setDraftOrder([...columnOrder]);
              return ord.map((key) => (
                <Box key={key} sx={{ py: 1, px: 2 }}
                  draggable
                  onDragStart={(e)=>{ e.dataTransfer.setData('text/plain', key); }}
                  onDragOver={(e)=>{ e.preventDefault(); }}
                  onDrop={(e)=>{ 
                    e.preventDefault(); 
                    const src = e.dataTransfer.getData('text/plain'); 
                    if (!src || src===key) return; 
                    setDraftOrder(prev=>{ 
                      const cur = [...(prev||ord)]; 
                      const from = cur.indexOf(src); 
                      const to = cur.indexOf(key); 
                      if (from<0||to<0) return cur; 
                      cur.splice(to,0,cur.splice(from,1)[0]); 
                      return cur; 
                    }); 
                  }}
                >
                  <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <IconButton onClick={()=>setDraftColumnsVisible(prev=>({ ...(prev||{}), [key]: !((prev||list)[key] !== false) }))} size="small" sx={{ p: 0 }} aria-label="toggle column">
                        <Box component="img" src={(draftColumnsVisible ?? columnsVisible)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} alt="check" sx={{ width: 23, height: 24 }} />
                      </IconButton>
                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>{COLUMNS_LABELS[key] || key.toUpperCase()}</Typography>
                    </Box>
                    <DragIndicator fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                  </Box>
                </Box>
              )); 
            })()}
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Button variant="text" onClick={()=>{ setColsOpen(false); setColResizeMode(false); setDraftColumnsVisible(null); setDraftOrder(null); }} sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}>Anuluj</Button>
            <Button 
              variant="contained" 
              sx={{ width: 123, height: 40, borderRadius: '4px', textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: 14, backgroundColor: '#020242', '&:hover': { backgroundColor: '#020242' } }} 
              onClick={()=>{ 
                if (draftColumnsVisible) { 
                  setColumnsVisible(draftColumnsVisible); 
                  try { 
                    localStorage.setItem('contractors_columns_visible', JSON.stringify(draftColumnsVisible)); 
                  } catch {} 
                } 
                if (draftOrder) { 
                  const normalized = draftOrder.filter((k)=>ALL_COLUMNS.includes(k)).concat(ALL_COLUMNS.filter(k=>!draftOrder.includes(k))); 
                  setColumnOrder(normalized); 
                  try { 
                    localStorage.setItem('contractors_columns_order', JSON.stringify(normalized)); 
                  } catch {} 
                } 
                setColsOpen(false); 
                setColResizeMode(false); 
                setDraftColumnsVisible(null);
                setDraftOrder(null);
              }}
            >
              Zapisz zmiany
            </Button>
          </Box>
        </Paper>
      </Backdrop>
    </Box>
  );
};

export default ContractorsTab;
